import { useState } from "react";

import { Link, LoaderFunctionArgs, useLoaderData, useOutletContext, useParams, useRouteError } from "react-router";

import { AlertCircleIcon, CalendarIcon, ClockIcon, EditIcon, LockIcon, MessageSquareIcon, MoreHorizontalIcon, RefreshCwIcon, TagIcon, Trash2Icon, UserIcon } from "lucide-react";

import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";

import { Project } from "@/features/projects/types";
import DeleteTicketForm from "@/features/tickets/components/DeleteTicketForm";
import { TicketCommentsPanel } from "@/features/tickets/components/TicketCommentsPanel";
import { TicketHistoryPanel } from "@/features/tickets/components/TicketHistoryPanel";
import { ENV_COLOURS, PRIORITY_CONFIG, STATUS_CONFIG, TYPE_CONFIG } from "@/features/tickets/config";
import { getTicketById } from "@/features/tickets/services/ticket";
import { Ticket as TicketType } from "@/features/tickets/types/index";
import { EmptyState } from "@/shared/components/EmptyState";
import { PermissionTooltip } from "@/shared/components/PermissionTooltip";
import { SectionHeading } from "@/shared/components/SectionHeading";
import { PERMISSIONS } from "@/shared/lib/constants/permissions";
import { QUERY_EXPIRATIONS, QUERY_KEYS } from "@/shared/lib/constants/queries";
import { formatAbsoluteDateTime, formatRelativeDate } from "@/shared/lib/utils/date";
import { AppError, getRouteErrorMessage } from "@/shared/lib/utils/errors";
import queryClient from "@/shared/lib/utils/queryClient";

export async function ticketLoader({ params }: LoaderFunctionArgs) {
  const ticket = await queryClient.fetchQuery<TicketType, AppError | Error>({
    queryKey: [QUERY_KEYS.GET_TICKET_BY_ID, params.id, params.ticketId],
    queryFn: () => getTicketById(params.id!, params.ticketId!),
    staleTime: QUERY_EXPIRATIONS.GET_TICKET_BY_ID,
    retry: false,
  });

  return { ticket };
}

function TicketHeaderSkeleton() {
  return (
    <div className="flex flex-col gap-1 sm:flex-row sm:items-end sm:justify-between">
      <div className="flex flex-col gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <Skeleton className="h-4 w-14" />
          <Skeleton className="h-4 w-16" />
          <Skeleton className="h-4 w-14" />
        </div>
        <Skeleton className="h-6 w-72" />
        <div className="flex flex-wrap items-center gap-1.5">
          <Skeleton className="h-5 w-16" />
          <Skeleton className="h-5 w-20" />
        </div>
        <div className="flex flex-wrap items-center gap-4 pt-1">
          <Skeleton className="h-3.5 w-28" />
          <Skeleton className="h-3.5 w-28" />
          <Skeleton className="h-3.5 w-20" />
        </div>
      </div>
      <div className="flex items-end gap-2">
        <Skeleton className="h-8 w-24" />
        <Skeleton className="h-8 w-8" />
      </div>
    </div>
  );
}

function TicketBodySkeleton() {
  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_300px]">
      <div className="flex flex-col gap-6">
        <Card className="gap-2">
          <CardHeader className="pb-3">
            <Skeleton className="h-4 w-24" />
          </CardHeader>
          <CardContent className="flex flex-col gap-2">
            <Skeleton className="h-3.5 w-full" />
            <Skeleton className="h-3.5 w-full" />
            <Skeleton className="h-3.5 w-3/4" />
          </CardContent>
        </Card>
        <Card className="p-0 gap-0">
          <CardHeader className="px-4 py-3">
            <Skeleton className="h-8 w-48" />
          </CardHeader>
          <div className="px-6 pb-6 pt-2 flex flex-col gap-4">
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-3.5 w-full" />
            <Skeleton className="h-3.5 w-2/3" />
          </div>
        </Card>
      </div>
      <div className="flex flex-col gap-4">
        <Card className="gap-2">
          <CardHeader className="pb-3">
            <Skeleton className="h-4 w-12" />
          </CardHeader>
          <CardContent className="flex flex-col gap-4">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="flex flex-col gap-2">
                <Skeleton className="h-3 w-16" />
                <Skeleton className="h-4 w-24" />
                {i < 5 && <Separator className="mt-2" />}
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export function TicketLoading() {
  return (
    <div className="flex flex-col gap-6">
      <TicketHeaderSkeleton />
      <TicketBodySkeleton />
    </div>
  );
}

export function TicketError() {
  const { id } = useParams();
  const error = useRouteError();
  const { title, description, status } = getRouteErrorMessage(error);

  return (
    <div className="flex flex-col items-center gap-3 py-12 text-center">
      <div className="flex size-10 items-center justify-center bg-destructive/10">
        <AlertCircleIcon className="size-5 text-destructive" />
      </div>
      <div className="flex flex-col gap-1.5">
        <p className="text-sm font-semibold">{title}</p>
        <p className="max-w-xs text-sm text-muted-foreground">{description}</p>
      </div>
      {status !== undefined && <span className="font-mono text-xs font-semibold text-destructive">HTTP {status}</span>}
      <div className="flex items-center gap-2">
        <Button variant="outline" size="sm" onClick={() => window.location.reload()}>
          <RefreshCwIcon />
          Try again
        </Button>
        <Button asChild variant="outline" size="sm">
          <Link to={`/projects/${id}/tickets`}>Back to tickets</Link>
        </Button>
      </div>
    </div>
  );
}

export default function Ticket() {
  const { id, ticketId } = useParams();
  const { ticket } = useLoaderData() as { ticket: TicketType };
  const { permissions } = useOutletContext<{ project: Project; permissions: string[] }>();

  const canReadTickets = permissions.includes(PERMISSIONS.PROJECT_TICKETS_READ);
  const canUpdateTicket = permissions.includes(PERMISSIONS.PROJECT_TICKETS_UPDATE);
  const canDeleteTicket = permissions.includes(PERMISSIONS.PROJECT_TICKETS_DELETE);
  const canReadHistory = permissions.includes(PERMISSIONS.PROJECT_TICKET_HISTORY_READ);
  const canReadComments = permissions.includes(PERMISSIONS.PROJECT_TICKET_COMMENTS_READ);
  const canCreateComments = permissions.includes(PERMISSIONS.PROJECT_TICKET_COMMENTS_CREATE);
  const canUpdateComments = permissions.includes(PERMISSIONS.PROJECT_TICKET_COMMENTS_UPDATE);
  const canDeleteComments = permissions.includes(PERMISSIONS.PROJECT_TICKET_COMMENTS_DELETE) || permissions.includes(PERMISSIONS.PROJECT_TICKET_COMMENTS_DELETE_OWN);
  const canLikeComments = permissions.includes(PERMISSIONS.PROJECT_TICKET_COMMENTS_LIKE);

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [activityTab, setActivityTab] = useState("comments");
  const [historyTabOpened, setHistoryTabOpened] = useState(false);

  if (!canReadTickets) {
    return (
      <EmptyState
        icon={LockIcon}
        title="No access"
        description="You don't have permission to view tickets in this project. Contact an administrator to request access."
        action={
          <Button asChild variant="outline" size="sm">
            <Link to={`/projects/${id}`}>Back to project</Link>
          </Button>
        }
      />
    );
  }

  const typeCfg = TYPE_CONFIG[ticket.type];
  const TypeIcon = typeCfg.icon;
  const priCfg = ticket.priority ? PRIORITY_CONFIG[ticket.priority] : null;
  const PriIcon = priCfg?.icon ?? null;
  const statusCfg = STATUS_CONFIG[ticket.status];
  const StatusIcon = statusCfg.icon;

  const createdByLabel = ticket.createdBy ? `${ticket.createdBy.firstName ?? ""} ${ticket.createdBy.lastName ?? ""}`.trim() : null;
  const createdByInitials = ticket.createdBy?.firstName && ticket.createdBy?.lastName ? `${ticket.createdBy.firstName[0]}${ticket.createdBy.lastName[0]}`.toUpperCase() : null;
  const assigneeLabel = ticket.assignee ? `${ticket.assignee.firstName} ${ticket.assignee.lastName}` : null;
  const assigneeInitials = ticket.assignee ? `${ticket.assignee.firstName[0]}${ticket.assignee.lastName[0]}`.toUpperCase() : null;

  const createdAtAbsolute = ticket.createdAt.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  const updatedAtAbsolute = ticket.updatedAt ? ticket.updatedAt.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : null;

  return (
    <div className="flex flex-col gap-6">
      {/* Ticket header */}
      <div className="flex flex-col gap-1 sm:flex-row sm:items-end sm:justify-between">
        <div className="flex flex-col gap-3">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-mono text-xs text-muted-foreground">{ticket.key}</span>
            <span className={`flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider ${typeCfg.className}`}>
              <TypeIcon className="size-3" />
              {ticket.type}
            </span>
            {priCfg && PriIcon && (
              <span className={`flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider ${priCfg.className}`}>
                <PriIcon className="size-3" />
                {ticket.priority}
              </span>
            )}
            <span className={`flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider ${statusCfg.className}`}>
              <StatusIcon className="size-3" />
              {ticket.status}
            </span>
          </div>
          <h2 className="font-heading text-xl font-semibold tracking-normal leading-tight">{ticket.title}</h2>
          {ticket.tags.length > 0 && (
            <div className="flex flex-wrap items-center gap-1.5">
              {ticket.tags.map((tag) => (
                <span key={tag} className="flex items-center gap-1 border border-border bg-muted px-1.5 py-0.5 text-[10px] font-semibold tracking-wider uppercase text-muted-foreground">
                  <TagIcon className="size-2.5 shrink-0" />
                  {tag}
                </span>
              ))}
            </div>
          )}
          <div className="flex flex-wrap items-center gap-4 pt-1">
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="flex items-center gap-1.5 text-xs text-muted-foreground cursor-default">
                  <CalendarIcon className="size-3.5 shrink-0" />
                  Created {formatRelativeDate(ticket.createdAt)}
                </span>
              </TooltipTrigger>
              <TooltipContent>{formatAbsoluteDateTime(ticket.createdAt)}</TooltipContent>
            </Tooltip>
            {ticket.updatedAt && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="flex items-center gap-1.5 text-xs text-muted-foreground cursor-default">
                    <ClockIcon className="size-3.5 shrink-0" />
                    Updated {formatRelativeDate(ticket.updatedAt)}
                  </span>
                </TooltipTrigger>
                <TooltipContent>{formatAbsoluteDateTime(ticket.updatedAt)}</TooltipContent>
              </Tooltip>
            )}
            <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <MessageSquareIcon className="size-3.5 shrink-0" />
              {ticket.commentCount} {ticket.commentCount === 1 ? "comment" : "comments"}
            </span>
          </div>
        </div>
        <div className="flex items-end gap-2">
          <PermissionTooltip allowed={canUpdateTicket} message="You don't have permission to edit this ticket.">
            <Button variant="outline" size="sm" asChild={canUpdateTicket}>
              {canUpdateTicket ? (
                <Link to={`/projects/${id}/tickets/${ticketId}/edit`}>
                  <EditIcon className="size-3.5" />
                  Edit ticket
                </Link>
              ) : (
                <>
                  <EditIcon className="size-3.5" />
                  Edit ticket
                </>
              )}
            </Button>
          </PermissionTooltip>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="icon-sm">
                <MoreHorizontalIcon className="size-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-44">
              <DropdownMenuLabel className="normal-case tracking-normal text-xs font-normal text-muted-foreground">{ticket.key}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <PermissionTooltip allowed={canUpdateTicket} message="You don't have permission to edit this ticket." side="left">
                <DropdownMenuItem disabled={!canUpdateTicket} asChild={canUpdateTicket}>
                  {canUpdateTicket ? (
                    <Link to={`/projects/${id}/tickets/${ticketId}/edit`}>
                      <EditIcon />
                      Edit ticket
                    </Link>
                  ) : (
                    <>
                      <EditIcon />
                      Edit ticket
                    </>
                  )}
                </DropdownMenuItem>
              </PermissionTooltip>
              <DropdownMenuSeparator />
              <PermissionTooltip allowed={canDeleteTicket} message="You don't have permission to delete this ticket." side="left">
                <DropdownMenuItem variant="destructive" disabled={!canDeleteTicket} onClick={() => setDeleteOpen(true)}>
                  <Trash2Icon />
                  Delete ticket
                </DropdownMenuItem>
              </PermissionTooltip>
            </DropdownMenuContent>
          </DropdownMenu>
          <DeleteTicketForm open={deleteOpen} onOpenChange={setDeleteOpen} projectId={id!} ticket={ticket} permissions={permissions} />
        </div>
      </div>

      {/* Two-column layout */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_300px]">
        {/* ── Left column ── */}
        <div className="flex flex-col gap-6">
          <Card className="gap-2">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm">Description</CardTitle>
            </CardHeader>
            <CardContent>{ticket.description ? <p className="text-sm text-foreground/90 leading-relaxed whitespace-pre-wrap">{ticket.description}</p> : <p className="text-sm text-muted-foreground italic">No description provided.</p>}</CardContent>
          </Card>

          {ticket.type === "Bug" && (
            <Card className="gap-2">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm">Bug details</CardTitle>
                <CardDescription>Steps to reproduce and expected vs actual behaviour.</CardDescription>
              </CardHeader>
              <CardContent>
                <Tabs defaultValue="steps">
                  <TabsList className="mb-4 h-full w-full flex-col gap-1 bg-muted p-1 sm:flex-row">
                    <TabsTrigger value="steps" className="w-full justify-center data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-none">
                      Steps to reproduce
                    </TabsTrigger>
                    <TabsTrigger value="expected" className="w-full justify-center data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-none">
                      Expected vs. actual
                    </TabsTrigger>
                  </TabsList>
                  <TabsContent value="steps">{ticket.reproducingSteps ? <p className="text-sm text-foreground/90 leading-relaxed whitespace-pre-wrap">{ticket.reproducingSteps}</p> : <p className="text-sm text-muted-foreground italic">No steps provided.</p>}</TabsContent>
                  <TabsContent value="expected">
                    <div className="flex flex-col gap-4">
                      <div className="flex flex-col gap-1.5">
                        <SectionHeading>Expected behaviour</SectionHeading>
                        {ticket.expectedBehaviour ? <p className="text-sm text-foreground/90 leading-relaxed whitespace-pre-wrap">{ticket.expectedBehaviour}</p> : <p className="text-sm text-muted-foreground italic">Not specified.</p>}
                      </div>
                      <Separator />
                      <div className="flex flex-col gap-1.5">
                        <SectionHeading>Actual behaviour</SectionHeading>
                        {ticket.actualBehaviour ? <p className="text-sm text-foreground/90 leading-relaxed whitespace-pre-wrap">{ticket.actualBehaviour}</p> : <p className="text-sm text-muted-foreground italic">Not specified.</p>}
                      </div>
                    </div>
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>
          )}

          {/* Comments / History - posting, listing, replying, editing, deleting and liking are all wired */}
          <Card className="p-0 gap-0">
            <Tabs
              value={activityTab}
              onValueChange={(value) => {
                setActivityTab(value);
                if (value === "history") {
                  setHistoryTabOpened(true);
                }
              }}
            >
              <CardHeader className="px-4 py-3">
                <TabsList className="h-full w-fit flex-row gap-1 bg-muted p-1">
                  <TabsTrigger value="comments" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-none">
                    <MessageSquareIcon className="size-3.5" />
                    Comments
                    <Badge variant={null} className="ml-1 px-1.5 py-0 text-[10px]">
                      {ticket.commentCount}
                    </Badge>
                  </TabsTrigger>
                  <TabsTrigger value="history" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-none">
                    <ClockIcon className="size-3.5" />
                    History
                    <Badge variant={null} className="ml-1 px-1.5 py-0 text-[10px]">
                      {ticket.historyCount}
                    </Badge>
                  </TabsTrigger>
                </TabsList>
              </CardHeader>
              <TabsContent value="comments" className="mt-0">
                <TicketCommentsPanel projectId={id!} ticketId={ticketId!} canReadComments={canReadComments} canCreateComments={canCreateComments} canUpdateComments={canUpdateComments} canDeleteComments={canDeleteComments} canLikeComments={canLikeComments} />
              </TabsContent>
              <TabsContent value="history" className="mt-0">
                <TicketHistoryPanel projectId={id!} ticketId={ticketId!} canReadHistory={canReadHistory} enabled={historyTabOpened} />
              </TabsContent>
            </Tabs>
          </Card>
        </div>

        {/* ── Right column (sidebar) ── */}
        <div className="flex flex-col gap-4">
          <Card className="gap-2">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm">Details</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col gap-4">
              <div className="flex flex-col gap-2">
                <SectionHeading>Status</SectionHeading>
                <div className={`flex items-center gap-1.5 text-xs font-semibold ${statusCfg.className}`}>
                  <StatusIcon className="size-3.5 shrink-0" />
                  {ticket.status}
                </div>
              </div>
              <Separator />
              <div className="flex flex-col gap-2">
                <SectionHeading>Assignee</SectionHeading>
                {assigneeLabel && assigneeInitials ? (
                  <div className="flex items-center gap-2">
                    <Avatar size="sm">
                      <AvatarFallback className="text-[10px]">{assigneeInitials}</AvatarFallback>
                    </Avatar>
                    <span className="text-xs font-medium">{assigneeLabel}</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <div className="flex size-6 items-center justify-center bg-muted">
                      <UserIcon className="size-3 text-muted-foreground" />
                    </div>
                    <span className="text-xs text-muted-foreground">Unassigned</span>
                  </div>
                )}
              </div>
              <Separator />
              <div className="flex flex-col gap-2">
                <SectionHeading>Priority</SectionHeading>
                {priCfg && PriIcon ? (
                  <div className={`flex items-center gap-1.5 text-xs font-semibold ${priCfg.className}`}>
                    <PriIcon className="size-3.5 shrink-0" />
                    {ticket.priority}
                  </div>
                ) : (
                  <span className="text-xs text-muted-foreground">Not set</span>
                )}
              </div>
              <Separator />
              <div className="flex flex-col gap-2">
                <SectionHeading>Type</SectionHeading>
                <div className={`flex items-center gap-1.5 text-xs font-semibold ${typeCfg.className}`}>
                  <TypeIcon className="size-3.5 shrink-0" />
                  {ticket.type}
                </div>
              </div>
              <Separator />
              <div className="flex flex-col gap-2">
                <SectionHeading>Reporter</SectionHeading>
                {createdByLabel ? (
                  <div className="flex items-center gap-2">
                    <Avatar size="sm">
                      <AvatarFallback className="text-[10px]">{createdByInitials}</AvatarFallback>
                    </Avatar>
                    <p className="text-xs font-medium leading-none">{createdByLabel}</p>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <div className="flex size-6 items-center justify-center bg-muted">
                      <UserIcon className="size-3 text-muted-foreground" />
                    </div>
                    <span className="text-xs italic text-muted-foreground">Deleted member</span>
                  </div>
                )}
              </div>
              <Separator />
              <div className="flex flex-col gap-2">
                <SectionHeading>Story points</SectionHeading>
                {ticket.effort !== null ? (
                  <div className="flex items-center gap-3">
                    <span className="flex size-6 items-center justify-center bg-primary/10 text-primary text-xs font-bold tabular-nums">{ticket.effort}</span>
                    <span className="text-xs text-muted-foreground">points</span>
                  </div>
                ) : (
                  <span className="text-xs text-muted-foreground">Not set</span>
                )}
              </div>
              {ticket.type === "Bug" && (
                <>
                  {ticket.environment && (
                    <>
                      <Separator />
                      <div className="flex flex-col gap-2">
                        <SectionHeading>Environment</SectionHeading>
                        <span className={`text-xs font-semibold ${ENV_COLOURS[ticket.environment]}`}>{ticket.environment}</span>
                      </div>
                    </>
                  )}
                  {ticket.affectedVersion && (
                    <>
                      <Separator />
                      <div className="flex flex-col gap-2">
                        <SectionHeading>Affected version</SectionHeading>
                        <span className="text-xs font-mono text-foreground">{ticket.affectedVersion}</span>
                      </div>
                    </>
                  )}
                </>
              )}
            </CardContent>
          </Card>

          <Card className="gap-2 border-primary/20 bg-primary/5">
            <CardHeader className="pb-3">
              <div className="flex items-center gap-2">
                <ClockIcon className="size-3.5 text-primary" />
                <CardTitle className="text-sm text-primary">Activity</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="flex flex-col gap-3">
              <div className="flex items-center justify-between">
                <span className="text-xs text-muted-foreground">Comments</span>
                <span className="text-xs font-semibold tabular-nums">{ticket.commentCount}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs text-muted-foreground">History events</span>
                <span className="text-xs font-semibold tabular-nums">{ticket.historyCount}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs text-muted-foreground">Created</span>
                <span className="text-xs font-semibold">{createdAtAbsolute}</span>
              </div>
              {updatedAtAbsolute && (
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">Last updated</span>
                  <span className="text-xs font-semibold">{updatedAtAbsolute}</span>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
